<template>
    <div class="container form-container p-4">
        <div class="text-3xl mb-4 pb-2 font-light flex items-center justify-between">
            <div data-cy="claims-title">{{ $t('admin.claims.title') }}</div>
            <div class="flex flex-col">
                <a-dropdown :trigger="['click']" class="shadow-none" data-cy="claim-import-export-dropdown">
                    <a-button style="margin-left: 8px">
                        {{ $t('claims.import-export.title') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onSelectClick">
                            <a-menu-item data-cy="claim-import-export-select" key="ExportClaim">
                                <DownloadOutlined />
                                {{ $t('claims.import-export.export') }}
                            </a-menu-item>
                            <a-menu-item data-cy="claim-import-export-select" key="ClaimRemoval">
                                <ExceptionOutlined />
                                {{ $t('claims.import-export.removal') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
            </div>
        </div>
        <admin-claims-table ref="ClaimsTable" :section_type="section_type" @onTableAction="onTableAction" />
        <a-drawer
            data-cy="claim-drawer"
            title="Claim Details"
            :width="720"
            :open="drawer_visible"
            :body-style="{ padding: '0px', paddingBottom: '10px' }"
            :destroyOnClose="true"
            :maskClosable="false"
            :rootClassName="'epic-' + moduleStateLink"
            @close="onClose"
        >
            <view-claim
                data-cy="claim-view"
                :claim="claim"
                :section_type="section_type"
                form_mode="drawer"
                @onDrawerClose="onClose"
            />
        </a-drawer>
        <modal-remove-claim-data @formSubmit="onRemoval" :processing="processing" ref="RemoveDataModal" />
    </div>
</template>

<script>
import AdminClaimsTable from '@/components/tables/Claims.vue';
import ViewClaim from '@/components/views/claims/Default.vue';
import ModalRemoveClaimData from '@/components/modals/RemoveClaimData.vue';

import FileManager from '@/utils/file.js';
import { DownloadOutlined, DownOutlined, ExceptionOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        AdminClaimsTable,
        ViewClaim,
        ModalRemoveClaimData,
        DownloadOutlined,
        DownOutlined,
        ExceptionOutlined,
    },
    props: {
        association_id: {
            type: String,
            default: '',
        },
        company_id: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            claim: {},
            drawer_visible: false,
            drawer_form_dirty: false,
            processing: false,
        };
    },
    computed: {
        section_type() {
            if (
                (this.association_id === '' || this.association_id === undefined || this.association_id === null) &&
                (this.company_id === '' || this.company_id === undefined || this.company_id === null)
            ) {
                return 'site_admin';
            } else if (this.company_id === '' || this.company_id === undefined || this.company_id === null) {
                return 'association';
            } else {
                return 'company';
            }
        },
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'claim';
        },
    },
    methods: {
        openDrawer(id) {
            let url = '/api/admin/claims/' + id;

            if (this.section_type == 'association') {
                url = '/api/associations/claims/' + id;
            }

            if (this.section_type == 'company') {
                url = '/api/companies/claims/' + id;
            }

            this.$http
                .get(url)
                .then((response) => {
                    let claimData = {
                        ...response.data.data,
                        is_hide_claim_description: response.data.data.is_hide_claim_description == 1 ? true : false,
                    };

                    this.claim = claimData;
                    this.drawer_visible = true;
                })
                .catch((error) => {
                    if (error.response.status == 404) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.404'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCVIEW02' }),
                        });
                    }
                });
        },
        onTableAction(data) {
            switch (data.key) {
                case 'view':
                    this.openDrawer(data.id);
                    break;
            }
        },
        onClose() {
            this.drawer_visible = false;
        },
        onSelectClick(event) {
            switch (event.key) {
                case 'ExportClaim':
                    this.onExport();
                    break;
                case 'ClaimRemoval':
                    this.$refs.RemoveDataModal.showModal();
                    break;
            }
        },
        onExport() {
            if (this.processing) {
                return false;
            }

            let url = '/api/admin/claims/export';

            if (this.section_type == 'association') {
                url = '/api/associations/claims/export';
            }

            let data = this.$refs.ClaimsTable.params;

            this.$http
                .post(url, { ...data })
                .then((response) => {
                    if (
                        _.includes(
                            [
                                'text/csv; charset=UTF-8',
                                'text/csv; charset=utf-8',
                                'text/plain; charset=UTF-8',
                                'text/plain; charset=utf-8',
                            ],
                            response.headers['content-type'],
                        )
                    ) {
                        this.$notification.success({ message: this.$t('claims.notifications.success.export') });

                        FileManager.download(response);
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        // todo: add message here
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVEXC01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onRemoval(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/removal/claims', data, { headers })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.RemoveDataModal.modal_visible = false;
                        this.reload();

                        this.$notification.success({ message: response.data.message });
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCRD01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        reload() {
            this.$refs.ClaimsTable.reload();
        },
    },
};
</script>
